import { RefObject, useEffect, useRef } from 'react'

/**
 * Hook to manage scrollbar visibility on scroll.
 * Adds 'scrolling' class to the container while scrolling,
 * and removes it after scrolling stops.
 *
 * @param containerRef - Ref to the scrollable container element
 * @param timeoutMs - Time in milliseconds to wait before hiding scrollbar after scrolling stops (default: 1000)
 */
export function useScrollbarVisibility(
  containerRef: RefObject<HTMLElement | null>,
  timeoutMs: number = 1000
): void {
  const scrollTimeoutIdRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const scrollContainer = containerRef.current
    if (!scrollContainer) return

    const handleScrollWithVisibility = () => {
      const currentContainer = containerRef.current
      if (!currentContainer) return

      currentContainer.classList.add('scrolling')
      if (scrollTimeoutIdRef.current) {
        clearTimeout(scrollTimeoutIdRef.current)
      }
      scrollTimeoutIdRef.current = setTimeout(() => {
        const container = containerRef.current
        if (container) {
          container.classList.remove('scrolling')
        }
      }, timeoutMs)
    }

    scrollContainer.addEventListener('scroll', handleScrollWithVisibility)
    return () => {
      scrollContainer.removeEventListener('scroll', handleScrollWithVisibility)
      if (scrollTimeoutIdRef.current) {
        clearTimeout(scrollTimeoutIdRef.current)
      }
    }
  }, [timeoutMs])
}
